import { useMemo } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { buildEventWorkspaceHref, getMonitorProtection } from "./eventWorkspaceNavigation";
import {
  Activity,
  CalendarClock,
  HeartPulse,
  LayoutDashboard,
  NotebookPen,
  Radio,
  ShieldCheck,
} from "lucide-react";
import { useAppContext } from "@/contexts/AppContext";
import { isKnownPlacementTarget } from "@/config/navigationRegistry";
import { useDashboards } from "@/hooks/dashboard/useDashboards";
import type { DashboardRecord } from "@/lib/dashboard/types";
import type { SidebarItem } from "@/types/navigation";

/**
 * Event-first navigation for the Live Monitoring section.
 *
 *   Protection                /live/protection/campaigns
 *     ├─ Campaign schedule    /live/protection/campaigns/:campaignId
 *     └─ Event protection     /live/protection/events/:eventId
 *   Events                    /live/events
 *     ├─ Live events          /live/events
 *     ├─ Monitor              /live/events/:eventId/monitor
 *     └─ Overview             /live/events/:eventId/overview
 *   System health             /live/system-health
 */
export const LIVE_NAV: SidebarItem[] = [
  {
    id: "live-events",
    label: "Events",
    href: "/live/event-overview",
    children: [
      {
        id: "live-live-events",
        label: "Live events",
        href: "/live/event-overview",
        activePaths: ["/live/events", "/live/dashboards/event-overview"],
        icon: Radio,
      },
      {
        id: "live-event-monitor",
        label: "Per tenant",
        href: "/live/event-monitor",
        activePaths: [
          "/live/events/monitor",
          "/live/events/:eventId/monitor",
          "/live/dashboards/event-monitor",
        ],
      },
      {
        id: "live-event-overview",
        label: "All tenants",
        href: "/live/multi-chart",
        activePaths: [
          "/live/events/overview",
          "/live/events/:eventId/overview",
          "/live/dashboards/multi-chart",
        ],
      },
    ],
    icon: Activity,
  },
  {
    id: "live-protection",
    label: "Protection",
    href: "/live/campaign-schedule",
    children: [
      {
        id: "live-campaign-schedule",
        label: "Campaign schedule",
        href: "/live/campaign-schedule",
        activePaths: [
          "/live/protection/campaigns",
          "/live/protection/campaigns/:campaignId",
          "/live/dashboards/campaign-schedule",
        ],
        icon: CalendarClock,
      },
      {
        id: "live-event-protection",
        label: "Event protection",
        href: "/live/event-protection",
        activePaths: [
          "/live/protection/events",
          "/live/protection/events/:eventId",
          "/live/dashboards/event-protection",
        ],
        icon: ShieldCheck,
      },
    ],
    icon: ShieldCheck,
  },
  {
    id: "live-system-health",
    label: "System health",
    href: "/live/system-overview",
    activePaths: ["/live/system-health", "/live/dashboards/system-overview"],
    icon: HeartPulse,
  },
  {
    id: "live-activity",
    label: "Activity & notes",
    href: "/journal",
    basePath: "/journal",
    icon: NotebookPen,
  },
];

export function applyLivePlacements(
  items: SidebarItem[],
  dashboards: DashboardRecord[],
): SidebarItem[] {
  // These product destinations already render the stored dashboard at a stable route.
  // A placement of the same record must not add a second link to that page.
  const productSlugs = new Set([
    "campaign-schedule",
    "event-protection",
    "system-overview",
    "event-overview",
    "multi-chart",
    "event-monitor",
  ]);
  const seen = new Set<string>();
  const placed = dashboards.flatMap((dashboard) => {
    if (productSlugs.has(dashboard.slug)) return [];
    return (dashboard.placements ?? [])
      .filter((placement) => {
        const key = `${dashboard.id}:${placement.group_id}`;
        if (
          placement.section_id !== "live" ||
          !isKnownPlacementTarget(placement.section_id, placement.group_id) ||
          seen.has(key)
        )
          return false;
        seen.add(key);
        return true;
      })
      .map((placement) => ({ dashboard, placement }));
  });
  const compare = (left: (typeof placed)[number], right: (typeof placed)[number]) =>
    (left.placement.order ?? Number.MAX_SAFE_INTEGER) -
      (right.placement.order ?? Number.MAX_SAFE_INTEGER) ||
    left.dashboard.name.localeCompare(right.dashboard.name);

  const customDashboards = placed
    .filter(({ placement }) => placement.group_id === "dashboards")
    .sort(compare)
    .map(({ dashboard }): SidebarItem => ({
      id: `live-dashboard-${dashboard.id}`,
      label: dashboard.name,
      href: `/live/dashboards/${encodeURIComponent(dashboard.slug)}`,
      icon: LayoutDashboard,
    }));
  const groups =
    customDashboards.length > 0
      ? [
          ...items,
          {
            id: "live-dashboards",
            label: "Dashboards",
            href: customDashboards[0].href,
            icon: LayoutDashboard,
            children: customDashboards,
          },
        ]
      : items;
  return groups.map((item) => {
    const groupId = item.id.replace("live-", "");
    if (groupId === "dashboards") return item;
    const dashboardItems = placed
      .filter(({ placement }) => placement.group_id === groupId)
      .sort(compare)
      .map(({ dashboard }): SidebarItem => ({
        id: `live-dashboard-${dashboard.id}`,
        label: dashboard.name,
        href: `/live/${encodeURIComponent(dashboard.slug)}`,
        icon: LayoutDashboard,
      }));
    return dashboardItems.length > 0
      ? { ...item, children: [...(item.children ?? []), ...dashboardItems] }
      : item;
  });
}

/**
 * Analytics navigation with event-aware Monitor and Overview destinations.
 */
export function useLiveNav(): SidebarItem[] {
  const { eventWorkspaceSelection, recents } = useAppContext();
  const dashboards = useDashboards({ relationship: "accessible", state: "saved" });
  const location = useLocation();
  const match = useMatch("/live/events/:eventId/:view");
  const dashboardMatch = useMatch("/live/dashboards/:slug");
  const shortMatch = useMatch("/live/:slug");
  const dashboardSlug = dashboardMatch?.params.slug ?? shortMatch?.params.slug;
  const currentView =
    match?.params.view ??
    (location.pathname === "/live/events/overview" || dashboardSlug === "multi-chart"
      ? "overview"
      : location.pathname === "/live/events/monitor" || dashboardSlug === "event-monitor"
        ? "monitor"
        : undefined);
  const currentEventId =
    currentView === "monitor" || currentView === "overview"
      ? (match?.params.eventId ?? new URLSearchParams(location.search).get("event_id"))
      : undefined;

  return useMemo(() => {
    const eventId = currentEventId ?? eventWorkspaceSelection.eventId;
    const recent = recents.find((item) => item.kind === "event" && item.id === eventId);
    const view =
      currentView === "overview" ||
      (!currentEventId &&
        (recent?.href.endsWith("/overview") ||
          recent?.href.startsWith("/live/dashboards/multi-chart") ||
          recent?.href.startsWith("/live/multi-chart")))
        ? "overview"
        : "monitor";
    const protectionId = currentEventId ? getMonitorProtection(view, location.search) : null;
    const monitorHref = buildEventWorkspaceHref("monitor", eventId, protectionId);
    const overviewHref = buildEventWorkspaceHref("overview", eventId, protectionId);
    const eventAwareItems = LIVE_NAV.map((item) =>
      item.id === "live-events"
        ? {
            ...item,
            children: item.children?.map((child) => ({
              ...child,
              href:
                child.id === "live-event-overview"
                  ? overviewHref
                  : child.id === "live-event-monitor"
                    ? monitorHref
                    : child.href,
            })),
          }
        : item,
    );
    return applyLivePlacements(eventAwareItems, dashboards.data ?? []);
  }, [
    currentEventId,
    currentView,
    dashboards.data,
    eventWorkspaceSelection.eventId,
    location.search,
    recents,
  ]);
}
